import * as types from 'features/vehicles/vehiclesActions';
import { VehiclesAction } from 'features/vehicles/vehiclesActions';
import { REHYDRATE, RehydrateAction } from 'redux-persist';
import { CLAIM_STATE } from 'myParkdUtils/enums';
import { isSameLicensePlate, removeVehicleFromArray } from 'myParkdUtils/utils';
import { DeviceMode, LocalizedLicensePlate, PagingReactTable, Status, Vehicle, VehicleAndDriver } from 'types';
import { INITIAL_PAGING, STATUS_INITIAL, STATUS_LOADING, STATUS_SUCCEEDED } from 'myParkdUtils/constants';
import { STATUS_ERROR } from '@parkd/shared-front-end';
import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { updateColumnFilters } from 'screens/VehiclesScreen';


export type VehicleFilterProperty = 'filter' | 'licensePlate';
export type VehiclePredefinedFilter = 'nolocationupdates' | 'unclaimed' | 'blocked' | '';
export type VehicleFilters = {
    filter: VehiclePredefinedFilter,
    licensePlate: string
}

export type CompanyVehiclesState = {
    userVehicles: VehicleAndDriver[],
    notificationTotals: {
        blockedTotal: number,
        unclaimedTotal: number,
        noLocationUpdatesInLast30DaysTotal: number,
    },
    filters: ColumnFilter[],
    paging: PagingReactTable,
    sorting: ColumnSort[],
    isLoading: boolean,
    loaded: boolean,
    error: any,
    deviceUpdateStatus: Status,
    workingWeekUpdateStatus: Status,
    workingWeekDeleteStatus: Status,
}

export const defaultState: CompanyVehiclesState = {
    userVehicles: [],
    notificationTotals: {
        blockedTotal: 0,
        unclaimedTotal: 0,
        noLocationUpdatesInLast30DaysTotal: 0,
    },
    filters: [],
    paging: INITIAL_PAGING,
    sorting: [{ id: 'licensePlate', desc: false }],
    isLoading: false,
    loaded: false,
    error: null,
    deviceUpdateStatus: STATUS_INITIAL,
    workingWeekUpdateStatus: STATUS_INITIAL,
    workingWeekDeleteStatus: STATUS_INITIAL
};

export function removeDriverAfterUnclaim(vehiclesAndDriver: VehicleAndDriver[], unclaimedVehicleAndDriver: VehicleAndDriver): VehicleAndDriver[] {
    const vehiclesAndDriverAfterUnclaim = vehiclesAndDriver.map((vehicleAndDriver: VehicleAndDriver) => {
        if (JSON.stringify(vehicleAndDriver) === JSON.stringify(unclaimedVehicleAndDriver)) {
            return {
                vehicle: {
                    ...vehicleAndDriver.vehicle,
                    claimState: CLAIM_STATE.UNCLAIMED,
                },
                user: null
            };
        } else {
            return vehicleAndDriver;
        }
    });

    return vehiclesAndDriverAfterUnclaim;
}

export function updateRowAfterDeviceModeUpdate(vehiclesAndDriver: VehicleAndDriver[], localizedLicensePlate: LocalizedLicensePlate, deviceMode: DeviceMode): VehicleAndDriver[] {
    const vehiclesAndDriverAfterDeviceModeUpdate = vehiclesAndDriver.map((vehicleAndDriver: VehicleAndDriver) => {
        if (isSameLicensePlate(vehicleAndDriver.vehicle.localizedLicensePlate, localizedLicensePlate)) {
            return {
                vehicle: {
                    ...vehicleAndDriver.vehicle,
                    mode: deviceMode
                },
                user: vehicleAndDriver.user
            };
        } else {
            return vehicleAndDriver;
        }
    });

    return vehiclesAndDriverAfterDeviceModeUpdate;
}

export function replaceVehicle(vehiclesAndDriver: VehicleAndDriver[], updatedVehicle: Vehicle): VehicleAndDriver[] {
    return vehiclesAndDriver.map((vehicleAndDriver) => {
        if (isSameLicensePlate(vehicleAndDriver.vehicle.localizedLicensePlate, updatedVehicle.localizedLicensePlate)) {
            return {
                vehicle: updatedVehicle,
                user: vehicleAndDriver.user
            }
        }
        return vehicleAndDriver
    });
}

export default function companyVehicles(state: CompanyVehiclesState = defaultState, action: VehiclesAction | RehydrateAction): CompanyVehiclesState {
    switch (action.type) {
        case types.COMPANY_VEHICLES_REQUEST_START:
            if (action.payload.paging.currentPage === 0) {
                return {
                    ...defaultState,
                    filters: action.payload.filters,
                    sorting: action.payload.sorting,
                    paging: INITIAL_PAGING,
                    isLoading: true,
                };
            }
            return {
                ...state,
                isLoading: true,
            };

        case types.COMPANY_VEHICLES_REQUEST_COMPLETED:
            const userVehicles = action.payload?.companyVehicles?.userVehicles || [];
            const totalPages = action.payload?.companyVehicles?.totalPages || 1;
            const totalElements = action.payload?.companyVehicles?.totalElements || 0;
            const blockedTotal = action.payload?.companyVehicles?.blockedTotal || 0;
            const unclaimedTotal = action.payload?.companyVehicles?.unclaimedTotal || 0;
            const noLocationUpdatesInLast30DaysTotal = action.payload?.companyVehicles?.noLocationUpdatesInLast30DaysTotal || 0;

            return {
                ...state,
                userVehicles,
                paging: {
                    totalPages: totalPages,
                    totalElements: totalElements,
                },
                notificationTotals: {
                    blockedTotal: blockedTotal,
                    unclaimedTotal: unclaimedTotal,
                    noLocationUpdatesInLast30DaysTotal: noLocationUpdatesInLast30DaysTotal,
                },
                isLoading: false,
                loaded: true,
                error: null,
            };

        case types.COMPANY_VEHICLES_REQUEST_ERROR:
            return {
                ...state,
                isLoading: false,
                loaded: false,
                error: action.error,
            };

        //----------------------------------------------------------------------

        case types.UPDATE_FILTER_PROPERTY:
            let filters = updateColumnFilters(state.filters, action.payload?.id, action.payload?.value);
            return {
                ...state,
                filters
            };

        case types.UPDATE_DEVICE_MODE:
            return {
                ...state,
                deviceUpdateStatus: STATUS_LOADING
            }

        case types.UPDATE_DEVICE_MODE_COMPLETED:
            return {
                ...state,
                deviceUpdateStatus: STATUS_SUCCEEDED,
                userVehicles: updateRowAfterDeviceModeUpdate(
                    state.userVehicles,
                    action.payload.localizedLicensePlate,
                    action.payload.deviceMode
                )
            }

        case types.UPDATE_DEVICE_MODE_ERROR:
            return {
                ...state,
                deviceUpdateStatus: {
                    loading: false,
                    succeeded: false,
                    error: action.error
                }
            }

        case types.UPDATE_LICENSE_PLATE_PERMISSION_COMPLETED:
            return {
                ...state,
                userVehicles: replaceVehicle(state.userVehicles, { ...action.payload.vehicle, permission: action.payload.permission })
            }

        case types.UPDATE_COST_CENTER_COMPLETED:
            return {
                ...state,
                userVehicles: replaceVehicle(state.userVehicles, { ...action?.payload.vehicle, costCenter: action?.payload?.costCenter })
            }

        case types.UPDATE_LICENSE_PLATE_WORKING_WEEK:
            return {
                ...state,
                workingWeekUpdateStatus: STATUS_LOADING
            }

        case types.UPDATE_LICENSE_PLATE_WORKING_WEEK_COMPLETED:
            return {
                ...state,
                userVehicles: replaceVehicle(state.userVehicles, { ...action?.payload?.vehicle, workingWeek: action?.payload?.workingWeek }),
                workingWeekUpdateStatus: STATUS_SUCCEEDED
            }

        case types.UPDATE_LICENSE_PLATE_WORKING_WEEK_ERROR:
            return {
                ...state,
                workingWeekUpdateStatus: STATUS_ERROR
            }

        //----------------------------------------------------------------------

        case types.REMOVE_DRIVER_AFTER_UNCLAIM:
            const unclaimedVehicleAndUser = action.payload.vehicleAndUser;
            return {
                ...state,
                userVehicles: removeDriverAfterUnclaim(state.userVehicles, unclaimedVehicleAndUser),
            };

        //----------------------------------------------------------------------

        case types.REMOVE_VEHICLE_ROW_AFTER_DELETED:
            return {
                ...state,
                userVehicles: removeVehicleFromArray(state.userVehicles, action.payload.deletedVehicle)
            };

        //----------------------------------------------------------------------

        case REHYDRATE:
            return {
                ...state,
            };

        default:
            return state;
    }
}
